import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  UserIcon,
  ArrowLeftEndOnRectangleIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions } from "../../hooks/usePermissions";

export interface MenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  requiredRoles?: string[];
}

export interface SidebarSection {
  label: string;
  items: MenuItem[];
}

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  // #148: nav-config props so other shells (the SuperAdmin portal) render
  // this same token-styled sidebar instead of maintaining a divergent copy.
  // When omitted, the workspace nav below is used unchanged.
  sections?: SidebarSection[];
  /** Badge under the wordmark; defaults to "Answers". */
  badgeText?: string;
  /** Brand link target and the one href matched exactly; defaults to "/". */
  brandTo?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  mobileOpen,
  onCloseMobile,
  sections: sectionsProp,
  badgeText = "Answers",
  brandTo = "/",
}) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { hasAccess } = usePermissions();

  const allMenuItems: MenuItem[] = [
    { label: "Dashboard", icon: HomeIcon, href: "/" },
    { label: "Documents", icon: DocumentTextIcon, href: "/documents" },
    { label: "Search", icon: MagnifyingGlassIcon, href: "/search" },
    { label: "Chat", icon: ChatBubbleLeftRightIcon, href: "/chat" },
    { label: "Analytics", icon: ChartBarIcon, href: "/analytics" },
    {
      label: "Users",
      icon: UsersIcon,
      href: "/users",
      requiredRoles: ["ADMIN", "OWNER"],
    },
    {
      label: "Organizations",
      icon: BuildingOfficeIcon,
      href: "/admin/organizations",
      requiredRoles: ["SUPER_ADMIN"],
    },
    {
      label: "Email Testing",
      icon: EnvelopeIcon,
      href: "/admin/email-testing",
      requiredRoles: ["SUPER_ADMIN"],
    },
    { label: "Settings", icon: Cog6ToothIcon, href: "/settings" },
    { label: "Profile", icon: UserIcon, href: "/profile" },
  ];

  // Same sectioning + role gating as before, just restyled.
  const dashboardItem = allMenuItems.find((i) => i.label === "Dashboard")!;
  const profileItem = allMenuItems.find((i) => i.label === "Profile")!;
  const adminItems = allMenuItems.filter(
    (i) =>
      i.requiredRoles?.includes("ADMIN") &&
      !i.requiredRoles?.includes("SUPER_ADMIN"),
  );
  const superAdminItems = allMenuItems.filter((i) =>
    i.requiredRoles?.includes("SUPER_ADMIN"),
  );
  const regularItems = allMenuItems.filter(
    (i) => !i.requiredRoles && i.label !== "Dashboard" && i.label !== "Profile",
  );

  const mainMenuItems: MenuItem[] = [
    dashboardItem,
    ...adminItems.filter((i) => hasAccess(i.requiredRoles as any)),
    ...regularItems,
    profileItem,
  ];
  const visibleSuperAdminItems = superAdminItems.filter(
    () => user?.isSuperAdmin === true,
  );

  const isActive = (href: string) =>
    href === brandTo
      ? location.pathname === href
      : location.pathname.startsWith(href);

  const sections: SidebarSection[] = sectionsProp ?? [
    { label: "Workspace", items: mainMenuItems },
    ...(visibleSuperAdminItems.length > 0
      ? [{ label: "Super Admin", items: visibleSuperAdminItems }]
      : []),
  ];

  const renderMenuItem = (item: MenuItem) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    return (
      <li key={item.href}>
        <Link
          to={item.href}
          onClick={onCloseMobile}
          title={collapsed ? item.label : undefined}
          className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            collapsed ? "lg:justify-center" : ""
          } ${
            active
              ? "bg-sidebar-active text-white shadow-[inset_2px_0_0_var(--sidebar-accent)]"
              : "text-sidebar-text hover:bg-white/5 hover:text-white"
          }`}
        >
          <Icon className="h-[18px] w-[18px] flex-shrink-0" />
          <span className={collapsed ? "lg:hidden" : ""}>{item.label}</span>
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[238px] flex-col overflow-hidden bg-sidebar px-3.5 py-[18px] text-white transition-all duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${collapsed ? "lg:w-[68px]" : "lg:w-[238px]"}`}
      >
        {/* Brand */}
        <Link
          to={brandTo}
          onClick={onCloseMobile}
          className="mb-6 flex min-h-[44px] flex-col items-center justify-center gap-[7px] px-1.5"
        >
          {/* Expanded: wordmark + ANSWERS tag */}
          <div
            className={`flex flex-col items-center gap-[7px] ${
              collapsed ? "lg:hidden" : ""
            }`}
          >
            <img
              src="/refineai-wordmark.svg"
              alt="RefineAI"
              className="h-9 w-auto"
            />
            <span className="rounded-lg border border-[color-mix(in_srgb,var(--sidebar-accent)_45%,transparent)] px-3 py-1 indent-[0.34em] text-[11px] font-semibold uppercase tracking-[0.34em] text-sidebar-accent">
              {badgeText}
            </span>
          </div>
          {/* Collapsed: mark only */}
          <img
            src="/refineai-mark.svg"
            alt="RefineAI"
            className={`hidden h-8 w-auto ${collapsed ? "lg:block" : ""}`}
          />
        </Link>

        <nav className="flex-1 overflow-y-auto">
          {sections.map((section, index) => (
            <React.Fragment key={section.label}>
              <div
                className={`px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6E6E5A] ${
                  index > 0 ? "mt-5" : ""
                } ${collapsed ? "lg:hidden" : ""}`}
              >
                {section.label}
              </div>
              <ul className="space-y-0.5">
                {section.items.map(renderMenuItem)}
              </ul>
            </React.Fragment>
          ))}
        </nav>

        {/* Logout */}
        <button
          onClick={() => logout()}
          title={collapsed ? "Logout" : undefined}
          className={`mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-text transition-colors hover:bg-white/5 hover:text-white ${
            collapsed ? "lg:justify-center" : ""
          }`}
        >
          <ArrowLeftEndOnRectangleIcon className="h-[18px] w-[18px] flex-shrink-0" />
          <span className={collapsed ? "lg:hidden" : ""}>Logout</span>
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
